import React, { useState } from "react";
import { Card, CardBody, Container, Spinner } from "reactstrap";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import CommonButton from "../../../../Components/Common/CommonButton";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import AddEditDesign from "../components/AddEditDesign";
import { perPageData, showToastMessage } from "common/service/CommonService";
import { useDesignService } from "modules/Library/Design/api/useDesign";
import { queryClient } from "api/queryClient";
import Reason from "common/utilities/Reason/Reason";
import DeleteModal from "Components/Common/DeleteModal";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import { designColumns } from "../helper/design.constant";

const DesignList: React.FC = () => {
    document.title = "Design | ZENTIXS VISTA";
  /* ------------------------- STATE ------------------------- */
  const [searchText, setSearchText] = useState<string | null>(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [isReasonModal, setIsReasonModal] = useState(false);
  const [updatedRecord, setUpdatedRecord] = useState<any>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [showDeleteReason, setShowDeleteReason] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  /* ------------------------- API ------------------------- */
  const { SelectAllQuery, InsertMutation, UpdateMutation, DeleteMutation } =
    useDesignService(searchText, currentPageNumber, perPageData);

  /* ------------------------- DATA ------------------------- */
  const {
    data: queryResponse,
    isLoading,
    isFetching,
  } = SelectAllQuery as {
    data?: { data?: any[] };
    isLoading: boolean;
    isFetching: boolean;
  };

  const designs = queryResponse?.data ?? [];
  const totalCount = designs[0]?.total_count ?? 0;



  /* ------------------------- SEARCH ------------------------- */
  const onSearch = (text: string | null) => {
    setSearchText(text);
    setCurrentPageNumber(1);
    
  };

  const handleToggle = () => {
    setModalOpen(!modalOpen);
    if (modalOpen) setEditData(null);
  };

  /* ------------------------- EDIT ------------------------- */
  const handleEdit = (row: any) => {
    setEditData(row);
    setModalOpen(true);
  };

  /* ------------------------- DELETE ------------------------- */
  const handleDelete = (row: any) => {
    setSelectedRecord(row);
    setDeleteModal(true);
  };

  const confirmDelete = (payload: { id: number | null; reason: string }) => {
    DeleteMutation.mutate(
      {
        studydesignid: payload.id,
        updatereason: payload.reason,
      },
      {
        onSuccess: (response) => {
          showToastMessage(
            "success",
            response?.message || "Design deleted successfully",
          );
          queryClient.invalidateQueries({ queryKey: ["design-select-all"] });
          const totalRecords = designs[0]?.total_count || 0;
          const lastPage = Math.ceil((totalRecords - 1) / perPageData); 
          setCurrentPageNumber(
          Math.max(1, Math.min(currentPageNumber, lastPage)),
           ); 
          setDeleteModal(false);
          setShowDeleteReason(false);
          setSelectedRecord(null);
          setSearchText("");
        },
        onError: (error: any) => {
          showToastMessage("error", error || "Failed to delete design");
        },
      },
    );
  };

  /* ------------------------- SAVE ------------------------- */
  const onSave = (payload: any) => {
    if (editData) {
      // Update existing - Open Reason Modal
      setUpdatedRecord({ ...payload, studydesignid: editData.studydesignid });
      setIsReasonModal(true);
    } else {
      // Add new
      InsertMutation.mutate(payload, {
        onSuccess: (response) => {
          showToastMessage(
            "success",
            response?.message || "Design added successfully",
          );
          queryClient.invalidateQueries({ queryKey: ["design-select-all"] });
          setModalOpen(false);
          setEditData(null);
          setSearchText("");
          setCurrentPageNumber(1)
        },
        onError: (error: any) => {
          showToastMessage("error", error || "Failed to add design");
        },
      });
    }
  };

  const handleReasonSubmit = (reasonPayload: { reason: string }) => {
    const finalPayload = {
      ...updatedRecord,
      updatereason: reasonPayload.reason,
    };

    UpdateMutation.mutate(finalPayload, {
      onSuccess: (response) => {
        showToastMessage(
          "success",
          response?.message || "Design updated successfully",
        );
        queryClient.invalidateQueries({ queryKey: ["design-select-all"] });
        setIsReasonModal(false);
        setModalOpen(false);
        setEditData(null);
        setUpdatedRecord(null);
        setSearchText("");
        setCurrentPageNumber(1)
      },
      onError: (error: any) => {
        showToastMessage("error", error || "Failed to update design");
      },
    });
  };

  const onAuditClick = () => {
    const payload = {
      modulename: "Study Design",
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Design_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  return (
    <div className="page-content">
      <Container fluid className="px-0">
        <Card className="table_wrapper">
          <CardBody>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <CommonSearch onSearch={onSearch} searchText={searchText} />
              <div className="d-flex gap-2">
                <CommonButton
                  module={PERMISSION_MODULES.DESIGN}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  color="primary"
                  size="sm"
                  outline
                  module={PERMISSION_MODULES.DESIGN}
                  action={PERMISSION_ACTIONS.ADD}
                  onClick={() => setModalOpen(true)}
                >
                  <i className="ri-add-line align-middle me-1"></i> Add
                </CommonButton>
              </div>
            </div>

            <CommonTableIndexWise
              moduleName={PERMISSION_MODULES.DESIGN}
              columns={designColumns}
              data={designs}
              loading={SelectAllQuery.isFetching}
              pageSize={perPageData}
              currentPage={currentPageNumber}
              onPageChange={setCurrentPageNumber}
              totalRecords={totalCount}
              actions={{
                onEdit: handleEdit,
                onDelete: handleDelete,
              }}
            />
          </CardBody>
        </Card>

        <AddEditDesign
          isOpen={modalOpen}
          toggle={handleToggle}
          existingData={editData}
          onSave={onSave}
          isLoading={InsertMutation.isPending}
        />

        {isReasonModal && (
          <Reason
            isOpen={isReasonModal}
            toggle={() => setIsReasonModal(false)}
            action="Update"
            id={editData?.studydesignid}
            click={handleReasonSubmit}
            isLoading={UpdateMutation.isPending}
          />
        )}
      </Container>

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}

      {deleteModal && (
        <DeleteModal
          isopen={deleteModal}
          toggle={() => setDeleteModal(false)}
          recordId={selectedRecord?.studydesignid}
          recordName={selectedRecord?.designtitle}
          onDeleteClick={confirmDelete}
          isLoading={DeleteMutation.isPending}
          showDeleteReason={showDeleteReason}
          toggleDeleteReason={() => setShowDeleteReason(!showDeleteReason)}
        />
      )}
    </div>
  );
};

export default DesignList;
